/**
 * Real-time Service
 *
 * Bridges WebSocket events from Flight Director (via websocket.js transport)
 * to the pub/sub API that hooks consume. Falls back to a mock setInterval
 * simulation when VITE_USE_MOCK=true or VITE_WS_URL is not configured.
 *
 * Public API (`subscribe` / `unsubscribe`) is unchanged — existing hooks
 * and components work without modification.
 *
 * ## Legacy event types (mapped from Flight Director events)
 *
 * | Event                  | Payload shape                                       |
 * |------------------------|-----------------------------------------------------|
 * | `workflow:updated`     | `{ workflowId, status, currentStep, updatedAt }`    |
 * | `workflow:completed`   | `{ workflowId, completedAt }`                       |
 * | `workflow:failed`      | `{ workflowId, failedAt, reason }`                  |
 * | `step:updated`         | `{ workflowId, stepId, status, completedAt? }`      |
 *
 * Spec-021 queue lifecycle events map to the legacy events above so existing
 * hooks refetch: `workflow_queued`/`workflow_dequeued`/`workflow_queue_cancelled`
 * → `workflow:updated`; `workflow_queue_poisoned` → `workflow:failed`.
 *
 * ## Flight Director native events (forwarded as-is)
 *
 * | Event                       | Description                                    |
 * |-----------------------------|------------------------------------------------|
 * | `task_updated`              | Task status change                             |
 * | `task_created`              | New task                                       |
 * | `remote_agent_activity`     | Granular agent event (thinking, progress)      |
 * | `plan_update`               | Orchestration plan change                      |
 * | `tool_call`                 | Agent tool invocation                          |
 * | `tool_response`             | Agent tool result                              |
 * | `outgoing_agent_message`    | Message routed to agent                        |
 * | `file_uploaded`             | File artifact                                  |
 * | `file_processing_completed` | File analysis finished                         |
 * | `message_chunk`             | Token-by-token streaming                       |
 * | `agent_registered`          | Agent came online                              |
 * | `session_started`           | Backend session / restart detection            |
 * | `agent_registry_sync`       | Full agent list sent on connect                |
 * | `connection:status`         | Local connection lifecycle events               |
 */

import {
  connect as wsConnect,
  disconnect as wsDisconnect,
  subscribe as wsSubscribe,
  unsubscribe as wsUnsubscribe,
  isConnected as wsIsConnected,
  getSessionId as wsGetSessionId,
} from './websocket';

// ────────────────────────────────────────────────────────────────
// Configuration — reads runtime window._env_ first, then build-time
// import.meta.env, matching the pattern in websocket.js.
// ────────────────────────────────────────────────────────────────

function resolveEnv(key, fallback = '') {
  return window._env_?.[key] || import.meta.env[key] || fallback;
}

const USE_MOCK = resolveEnv('VITE_USE_MOCK') === 'true'
  || !resolveEnv('VITE_WS_URL');

// ────────────────────────────────────────────────────────────────
// Internal state
// ────────────────────────────────────────────────────────────────

/** @type {Map<string, Set<Function>>} */
const listeners = new Map();

let _bridgeActive = false;

// Mock-mode state
let _mockIntervalId = null;
let _mockTick = 0;
const MOCK_RUNNING_IDS = ['wf-001', 'wf-003'];

// ────────────────────────────────────────────────────────────────
// Event emission (internal)
// ────────────────────────────────────────────────────────────────

function _emit(event, payload) {
  const fns = listeners.get(event);
  if (!fns) return;
  fns.forEach((fn) => {
    try {
      fn(payload);
    } catch (err) {
      console.error(`[realtime] listener error on "${event}":`, err);
    }
  });
}

// ────────────────────────────────────────────────────────────────
// Flight Director → Mission Control event mapping
// ────────────────────────────────────────────────────────────────

/** FD event types that represent a workflow-level state change. */
const WORKFLOW_STATE_ACTIVITIES = new Set([
  'complete', 'completed', 'agent_complete',
  'error', 'failed', 'agent_error',
  'agent_start',
]);

/**
 * Map a Flight Director event to zero or more legacy MC events.
 * The mapped events trigger existing hook refetches.
 *
 * Only emits `workflow:updated` for events that genuinely change
 * workflow-level state. High-frequency events like agent thinking /
 * progress are limited to `step:updated` to avoid excessive refetches.
 *
 * @param {string} eventType — FD event name
 * @param {Object} data      — FD event payload
 * @returns {Array<[string, Object]>}
 */
function mapFdEvent(eventType, data) {
  const mapped = [];
  const contextId = data.contextId || data.context_id || data.conversationId || data.session_id || '';
  const now = data.timestamp || data.updated_at || new Date().toISOString();

  switch (eventType) {
    case 'task_updated': {
      const state = data.state || data.status;
      if (state === 'completed') {
        mapped.push(['workflow:completed', {
          workflowId: contextId,
          completedAt: now,
        }]);
      } else if (state === 'failed') {
        mapped.push(['workflow:failed', {
          workflowId: contextId,
          failedAt: now,
          reason: data.content || data.error || 'Task failed',
        }]);
      } else {
        mapped.push(['workflow:updated', {
          workflowId: contextId,
          status: state === 'working' ? 'running' : (state || 'running'),
          currentStep: data.current_step ?? data.currentStep ?? 0,
          updatedAt: now,
        }]);
      }
      // Also emit step-level update when an agent or task is identified
      if (data.agentName || data.taskId || data.task_id) {
        mapped.push(['step:updated', {
          workflowId: contextId,
          stepId: data.taskId || data.task_id || data.agentName,
          status: (state === 'working' ? 'running' : state) || 'running',
          completedAt: state === 'completed' ? now : undefined,
        }]);
      }
      break;
    }

    case 'task_created': {
      mapped.push(['workflow:updated', {
        workflowId: contextId,
        status: 'running',
        currentStep: 0,
        updatedAt: now,
      }]);
      break;
    }

    case 'remote_agent_activity': {
      const activity = data.activity || data.eventType;

      // Always emit step:updated for all activity
      if (activity === 'complete' || activity === 'completed' || activity === 'agent_complete') {
        mapped.push(['step:updated', {
          workflowId: contextId,
          stepId: data.task_id || data.agentName || data.agent || 'unknown',
          status: 'completed',
          completedAt: now,
        }]);
      } else if (activity === 'error' || activity === 'failed' || activity === 'agent_error') {
        mapped.push(['step:updated', {
          workflowId: contextId,
          stepId: data.task_id || data.agentName || data.agent || 'unknown',
          status: 'failed',
        }]);
      } else {
        mapped.push(['step:updated', {
          workflowId: contextId,
          stepId: data.agentName || data.agent || 'unknown',
          status: 'running',
        }]);
      }

      // Only emit workflow:updated for state-changing activities (start,
      // complete, error) — NOT for high-frequency thinking/progress events
      // which would cause excessive refetches in useWorkflows().
      if (WORKFLOW_STATE_ACTIVITIES.has(activity)) {
        mapped.push(['workflow:updated', {
          workflowId: contextId,
          status: 'running',
          currentStep: 0,
          updatedAt: now,
        }]);
      }
      break;
    }

    case 'plan_update': {
      mapped.push(['workflow:updated', {
        workflowId: contextId,
        status: 'running',
        currentStep: 0,
        updatedAt: now,
      }]);
      break;
    }

    case 'workflow_session_started': {
      // Spec 020 — a new workflow run entered the running set. Forward as
      // workflow:updated so AppContext refetches and the new row appears.
      mapped.push(['workflow:updated', {
        workflowId: contextId,
        status: 'running',
        currentStep: 0,
        updatedAt: now,
      }]);
      break;
    }

    case 'workflow_session_ended': {
      // Spec 020 — terminal lifecycle. final_status comes through; map to the
      // matching legacy event so the existing refetch + removal flow runs.
      const finalStatus = data.finalStatus || data.final_status;
      if (finalStatus === 'failed') {
        mapped.push(['workflow:failed', {
          workflowId: contextId,
          // Accept both camelCase (FD's _emit_session_ended) and snake_case
          // (defensive — other mappers in this file consistently accept both
          // casings; FD relays could in principle reshape the field name).
          failedAt: data.endedAt || data.ended_at || now,
          reason: 'workflow ended with status=failed',
        }]);
      } else {
        mapped.push(['workflow:completed', {
          workflowId: contextId,
          completedAt: data.endedAt || data.ended_at || now,
        }]);
      }
      break;
    }

    case 'workflow_queued': {
      // Spec 021 — a launch was accepted and enqueued but not yet dispatched.
      // Forward as workflow:updated (status 'pending') so AppContext refetches
      // and the queued row appears immediately in the All Workflows table and
      // the dashboard "pending"/queued KPI — instead of staying invisible until
      // the dispatcher picks it up.
      mapped.push(['workflow:updated', {
        workflowId: contextId,
        status: 'pending',
        currentStep: 0,
        updatedAt: now,
      }]);
      break;
    }

    case 'workflow_dequeued': {
      // Spec 021 — the dispatcher pulled the message and started the run. The
      // paired workflow_session_started event also refetches; this is the
      // belt-and-suspenders so the queued→running transition is never missed.
      mapped.push(['workflow:updated', {
        workflowId: contextId,
        status: 'running',
        currentStep: 0,
        updatedAt: now,
      }]);
      break;
    }

    case 'workflow_queue_cancelled': {
      // Spec 021 — operator cancelled a queued launch before dispatch. This is
      // purely a refetch trigger: the consuming hooks reload the list/row from
      // the API and render the authoritative status. We deliberately do NOT put
      // a status in the payload — the UI has no 'cancelled' bucket, so emitting
      // one here risks an unstyled badge if a consumer reads the payload
      // directly (PR #79 review).
      mapped.push(['workflow:updated', {
        workflowId: contextId,
        updatedAt: now,
      }]);
      break;
    }

    case 'workflow_queue_poisoned': {
      // Spec 021 — a queued launch exceeded the dequeue threshold and was moved
      // to the poison queue; the row is now FAILED. Map to workflow:failed so
      // the existing failure refetch/removal flow runs.
      mapped.push(['workflow:failed', {
        workflowId: contextId,
        failedAt: now,
        reason: data.reason || 'workflow queue message poisoned',
      }]);
      break;
    }

    case 'orchestration_event': {
      if (data.status === 'completed') {
        mapped.push(['workflow:completed', {
          workflowId: contextId,
          completedAt: data.completed_at || now,
        }]);
      } else if (data.status === 'failed') {
        mapped.push(['workflow:failed', {
          workflowId: contextId,
          failedAt: data.failed_at || now,
          reason: data.error || data.reason || 'Unknown error',
        }]);
      }
      break;
    }

    case 'tool_response': {
      const status = data.status === 'success' ? 'completed' : 'failed';
      mapped.push(['step:updated', {
        workflowId: contextId,
        stepId: `${data.agentName || 'unknown'}:${data.toolName || 'unknown'}`,
        status,
        completedAt: status === 'completed' ? now : undefined,
      }]);
      break;
    }

    case 'tool_call': {
      mapped.push(['step:updated', {
        workflowId: contextId,
        stepId: `${data.agentName || 'unknown'}:${data.toolName || 'unknown'}`,
        status: 'running',
      }]);
      break;
    }

    // Events that don't map to legacy MC events (but are still
    // forwarded as raw FD events via the bridge):
    // session_started, agent_registry_sync, outgoing_agent_message,
    // file_uploaded, file_processing_completed, message_chunk, agent_registered
  }

  return mapped;
}

// ────────────────────────────────────────────────────────────────
// WebSocket → realtime bridge (delegates to websocket.js)
// ────────────────────────────────────────────────────────────────

/**
 * Handler for all WS events via the '*' catch-all from websocket.js.
 * Forwards raw FD events and emits mapped legacy MC events.
 */
function onWsEvent(data) {
  const eventType = data.type || data.eventType;
  if (!eventType) return;

  // 1. Forward the raw FD event to realtime listeners
  _emit(eventType, data);

  // 2. Emit mapped legacy MC events for existing hooks
  const mapped = mapFdEvent(eventType, data);
  for (const [mcEvent, mcPayload] of mapped) {
    _emit(mcEvent, mcPayload);
  }
}

/**
 * Normalize connection statuses from websocket.js into the set
 * that realtime consumers can rely on.
 *
 * websocket.js emits: connected, disconnected, error, failed
 * We normalize to:    connected, disconnected, error, failed
 */
function onWsConnection(data) {
  _emit('connection:status', data);
}

function startBridge() {
  if (_bridgeActive) return;
  _bridgeActive = true;
  wsSubscribe('*', onWsEvent);
  wsSubscribe('connection', onWsConnection);

  // Only call wsConnect when not already connected — websocket.js
  // closes the existing socket on every connect() call, which would
  // interrupt other consumers and drop in-flight messages.
  if (!wsIsConnected()) {
    wsConnect(resolveEnv('VITE_FD_TENANT_ID') || undefined);
  }
}

function stopBridge() {
  if (!_bridgeActive) return;
  _bridgeActive = false;
  wsUnsubscribe('*', onWsEvent);
  wsUnsubscribe('connection', onWsConnection);
  wsDisconnect();
}

// ────────────────────────────────────────────────────────────────
// Mock simulation (fallback when no WS URL configured)
// ────────────────────────────────────────────────────────────────

function emitMockEvent() {
  _mockTick += 1;
  const workflowId = MOCK_RUNNING_IDS[_mockTick % MOCK_RUNNING_IDS.length];
  const now = new Date().toISOString();

  _emit('step:updated', {
    workflowId,
    stepId: `s${_mockTick}`,
    status: 'running',
  });

  if (_mockTick % 2 === 0) {
    _emit('workflow:updated', {
      workflowId,
      status: 'running',
      currentStep: 2 + (_mockTick % 3),
      updatedAt: now,
    });
  }
}

function startMock() {
  if (_mockIntervalId !== null) return;
  const jitter = 3000 + Math.random() * 2000;
  _mockIntervalId = setInterval(emitMockEvent, jitter);
}

function stopMock() {
  if (_mockIntervalId !== null) {
    clearInterval(_mockIntervalId);
    _mockIntervalId = null;
  }
}

// ────────────────────────────────────────────────────────────────
// Connection lifecycle helpers
// ────────────────────────────────────────────────────────────────

function hasListeners() {
  let total = 0;
  for (const fns of listeners.values()) total += fns.size;
  return total > 0;
}

function ensureConnection() {
  if (USE_MOCK) {
    startMock();
  } else {
    startBridge();
  }
}

function maybeDisconnect() {
  if (hasListeners()) return;
  if (USE_MOCK) {
    stopMock();
  } else {
    stopBridge();
  }
}

// ────────────────────────────────────────────────────────────────
// Public API
// ────────────────────────────────────────────────────────────────

/**
 * Register a callback for a real-time event.
 * Establishes the WebSocket bridge on first subscription.
 *
 * Works with both legacy MC events (`workflow:updated`, etc.) and
 * raw Flight Director events (`task_updated`, `plan_update`, etc.).
 *
 * @param {string} event
 * @param {Function} callback
 */
export function subscribe(event, callback) {
  if (!listeners.has(event)) {
    listeners.set(event, new Set());
  }
  listeners.get(event).add(callback);
  ensureConnection();
}

/**
 * Remove a previously registered callback.
 * Closes the WebSocket when all listeners are removed.
 *
 * @param {string} event
 * @param {Function} callback
 */
export function unsubscribe(event, callback) {
  const fns = listeners.get(event);
  if (fns) {
    fns.delete(callback);
    if (fns.size === 0) listeners.delete(event);
  }
  maybeDisconnect();
}

/**
 * Current WebSocket connection status.
 *
 * @returns {'connected'|'disconnected'|'error'|'failed'|'mock'}
 */
export function getConnectionStatus() {
  if (USE_MOCK) return 'mock';
  return wsIsConnected() ? 'connected' : 'disconnected';
}

/**
 * The Flight Director session ID (tracked by websocket.js transport).
 * Changes when FD restarts — useful for detecting stale state.
 *
 * @returns {string|null}
 */
export function getSessionId() {
  return wsGetSessionId();
}

/**
 * Whether the service is running in mock mode.
 * @returns {boolean}
 */
export function isMockMode() {
  return USE_MOCK;
}

/**
 * Force-disconnect and stop all activity.
 * Call this on app unmount or when navigating away.
 */
export function disconnect() {
  if (USE_MOCK) {
    stopMock();
  } else {
    stopBridge();
  }
}
